import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import { ZorroTableComponent } from '../../../components/common/custom-table/zorro-table.component';
import { SharedComponents } from '../../../components/shared.module';
import { ProjectService } from '../../../core/services/project/project.service';
import { RestService } from '../../../core/services/rest.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { MaterialModule } from '../../../core/shared/material.module';
import { testAgentConfig, testBrowserConfig, testBrowserSections } from '../../../core/table-templates/agent-browser-dropdown';
import { testManagementColumns } from '../../../core/table-templates/test-management-table';
import { MessageEnum } from '../../../core/utils/message.enum';
import { MenuDropdownComponent } from '../../../components/common/menu-dropdown/menu-dropdown.component';

@Component({
  selector: 'app-add-or-edit-test-plan',
  standalone: true,
  imports: [SharedComponents, CommonModule, MaterialModule, MenuDropdownComponent],
  templateUrl: './add-or-edit-test-plan.component.html',
  styleUrl: './add-or-edit-test-plan.component.scss'
})
export class AddOrEditTestPlanComponent implements OnInit, OnDestroy {

  @Input()
  data: { title?: string, id?: string } = {};
  showAgentOptions: boolean = false;
  @Input() testPlansList: any;
  planName: string = "";
  selectedItem: any = {
    id: ""
  };
  dataSource: any = [];
  columns: any = testManagementColumns;
  activeModal = inject(NgbActiveModal);
  agentConfig: any = testAgentConfig
  agentValue: string = "";
  agentSections: any = [];
  browserValue: string = "";
  browsersConfig: any = testBrowserConfig;
  browsersSections: any = testBrowserSections;
  treeData: any = [];
  projectId: any = '';
  automationProjectId!: string;
  treeSearch: string = "";
  isDataLoaded: boolean = false;
  @ViewChild(ZorroTableComponent) zorroTableComponent!: ZorroTableComponent;
  private messageService = inject(MessageService);
  selectedRows: any[] = [];
  searchResults: any;
  warningMessage: string = "";
  isActive: boolean = false;

  constructor(
    private restService: RestService,
    private projectService: ProjectService,
    private readonly toaster: ToasterService
  ) {
    this.projectService.getProjectId().subscribe((id) => {
      this.projectId = id;
    });
    this.projectService.getAutomationProjectId().subscribe((id: any) => {
      this.automationProjectId = id;
    });

  }


  ngOnInit(): void {
    this.getTestPlansAndTestSuits();
  }

  getTestPlanById(id: string) {
    this.restService.getTestPlanById(this.projectId, this.automationProjectId, id).subscribe(
      (response) => {
        this.isDataLoaded = true;
        let data = response.data;
        this.planName = data.name;
        let index = 1;
        data.testScripts.forEach((row: any) => {
          this.dataSource = [...this.dataSource, {
            "select": false,
            "displayName": row.name,
            "selectedBrowser": row.browser,
            "selectedAgent": row.agentId || 'onDemand',
            // rowid relevent changes
            "tcgTestCaseResultId": row?.tcgTestCaseResultId,
            "id": `${row.id}_row_${index}`,
            "idWithoutRow": row.id,
            "selectedVersion": row.version,
            "scriptId": row.scriptId,
            "versionList": this.buildVersions(row.versions)
          }];
          index++;
        });
        this.zorroTableComponent?.refreshTableData();
      },
      (error) => {
        this.isDataLoaded = true;
        this.toaster.pop({ type: 'error', text: MessageEnum.TEST_PLAN_LOAD_FAILURE });
      });
  }

  getAgentsWithStatus() {
    this.restService.getAgentsWithStatus(this.projectId).subscribe((response) => {
      let localAgents: { label: any; icon: string; value: any; }[] = [];
      let cloudAgents: { label: any; icon: string; value: any; }[] = [];
      let data = response?.data;
      data.forEach((agent: any) => {
        let currentAgent = {
          "label": agent.name,
          "icon": agent.status === "OFFLINE" ? "assets/images/agent/agent-dead.svg" : "assets/images/agent/agent-alive.svg",
          "value": agent.id,
          agentType: agent.type,
        };
        agent.type == 'LOCAL' ? localAgents.push(currentAgent) : cloudAgents.push(currentAgent);
      });

      // On Demand agent
      this.agentSections.push(
        {
          label: 'Devices',
          type: 'devices',
          options: [
            {
              label: 'Local',
              options: []
            },
            {
              label: 'Remote',
              options: []
            }
          ]
        },
        {
          label: 'Execution Engines',
          type: 'agents',
          options: [
            {
              label: "On Demand",
              options: [
                {
                  label: 'On Demand',
                  icon: "assets/images/agent/agent-alive.svg",
                  value: 'onDemand',
                  agentType: 'CLUSTER'
                }
              ]
            },
            {
              label: 'Local Execution Engines',
              options: localAgents
            },
            {
              label: 'Cloud Based Execution Engines',
              options: cloudAgents
            }
          ]
        }
      )
      this.getDeviceList();
      if (this.data.id) {
        this.getTestPlanById(this.data.id);
      } else {
        this.isDataLoaded = true;
      }
    }, (error) => {
      this.isDataLoaded = true;
      this.toaster.pop({ type: 'error', text: MessageEnum.AGENT_LOAD_FAILURE });
    });
  }

  getDeviceList() {
    this.restService.getDevices(this.projectId).subscribe((response) => {
      let devices: { label: any; value: any; }[] = [];
      let data = response?.data;
      let local: any = {
        label: 'Local',
        options: []
      };
      let remote: any = {
        label: 'Remote',
        options: []
      };

      data.forEach((device: any) => {
        if(device.environment?.toUpperCase() === 'LOCAL') {
          local.options.push({
            label: device.name,
            value: device.id
          });
        } else {
          remote.options.push({
            label: device.name,
            value: device.id
          });
        }
      });
      this.agentSections[0].options[0].options = local.options;
      this.agentSections[0].options[1].options = remote.options;
      this.constructTable();
    });
  }

  getTestPlansAndTestSuits() {
    this.treeSearch = "";
    this.restService.getTestSuitsAndTestCases(this.projectId, this.automationProjectId).subscribe((response) => {
      let data = response.data?.filter((item: any) => item.type === 'SCRIPT');
      let suits: any = [];
      let cases: { id: any; name: any; scriptId: any; displayName: any; icon: string; order: number; level: number; type: any; active: any; children?: never[]; }[] = [];
      this.getAgentsWithStatus();
      data.forEach((item: any) => {
        let currentCase: any = {
          "id": item.id,
          "name": item.name,
          "scriptId": item.scriptId,
          "displayName": item.name,
          "tcgTestCaseResultId": item?.tcgTestCaseResultId ? item?.tcgTestCaseResultId : null,
          // "icon": "draft",
          "order": 0,
          "level": 2,
          "type": item.type,
          "active": item.active,
          "versions": item.versions,
          "dragable": true
        };
        if (item.automationScriptGroup) {
          let suit = {
            ...item.automationScriptGroup,
            children: [],
            // icon:'folder',
            dragable: false,
            displayName: item.automationScriptGroup.name,
            order: 0,
            level: 0
          };
          let index = suits.findIndex((suitItem: any) => suitItem.id === suit.id);

          if (index > -1) {
            suits[index].children.push(currentCase);
          } else {
            suit.children.push(currentCase);
            suits.push(suit)
          }
        } else {
          currentCase.order = 0;
          currentCase.level = 1;
          cases.push(currentCase);
        }
      });
      this.treeData = [...suits, ...cases]
    }, (error) => {
      this.isDataLoaded = true;
      this.toaster.pop({ type: 'error', text: MessageEnum.TEST_CASE_LOAD_FAILURE });
    });
  }

  constructTable() {
    //this.columns=testManagementColumns;
    let agentColumn = this.columns.filter((column: any) => {
      return column.key == 'agent'
    });
    let browserColumn = this.columns.filter((column: any) => { return column.key == 'browser' });
    if (agentColumn.length)
      agentColumn[0]._config.componentInputs.fields = this.agentSections;
    if (browserColumn.length)
      browserColumn[0]._config.componentInputs.fields = this.browsersSections;
  }

  toggleOptions(name: string) {
    this.showAgentOptions = !this.showAgentOptions;
  }
  setOption(name: string, value: string) {
    this.showAgentOptions = false;
  }

  selectCase(event: any) {
    if (event.dragable) {
      this.selectedItem = event;
    }
  }

  closePopup() {
    this.activeModal.dismiss('Cross click');
  }

  moveSelected(type: string) {
    if (type === 'add') {
      this.drop({
        item: {
          data: this.selectedItem
        }
      });
      this.selectedItem = {
        id: ""
      }
    } else {
      this.dataSource = this.dataSource.filter((row: any) => {
        return !this.selectedRows.includes(row.id);
      });
      let index = 1;
      this.dataSource.forEach((source: any) => {
        source.id = `${source.idWithoutRow}_row_${index}`;
        index++;
      })
      this.zorroTableComponent.refreshTableData();
      this.selectedRows = [];
    }
  }

  saveOrUpdate() {
    if (!this.dataSource.length)
      return;
    if (this.planName.includes('AdHocTestplan')) {
      this.toaster.pop({ type: 'error', text: MessageEnum.INVALID_PLAN_NAME });
      return;
    }
    let scripts = [];
    for (let i = 0; i < this.dataSource.length; i++) {
      let row = this.dataSource[i];
      if (!row.selectedAgent || !row.selectedBrowser || !row.selectedVersion) {
        this.toaster.pop({ type: 'error', text: MessageEnum.TEST_CASE_SELECTION_REQUIRED });
        return;
      }
      scripts.push({
        sequence: i,
        id: row.idWithoutRow,
        scriptId: row.scriptId,
        tcgTestCaseResultId: row?.tcgTestCaseResultId,
        name: row.displayName,
        increment: 3,
        agentId: row.selectedAgent === 'onDemand' ? null : row.selectedAgent,
        browser: row.selectedBrowser,
        version: row.selectedVersion,
      });
    }
    if (this.data.id) {
      if (this.isNameExists(this.planName, this.data.id)) {
        this.toaster.pop({ type: 'error', text: MessageEnum.TEST_PLAN_NAME_DUPLICATE });
      } else {
        this.restService
          .EditTestPlanById(
            this.projectId,
            this.automationProjectId,
            this.data.id,
            {
              name: this.planName,
              testScripts: scripts,
            }
          )
          .subscribe(
            (response) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.TEST_PLAN_UPDATE_SUCCESS });
              this.activeModal.close("refresh");
            },
            (error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.TEST_PLAN_UPDATE_FAILURE });
            }
          );
        return;
      }
    } else {
      if (this.isNameExists(this.planName)) {
        this.toaster.pop({ type: 'error', text: MessageEnum.TEST_PLAN_NAME_DUPLICATE });
      } else {
        this.restService
          .addNewTestPlan(this.projectId, this.automationProjectId, {
            name: this.planName,
            testScripts: scripts,
          })
          .subscribe(
            (response) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.TEST_PLAN_CREATE_SUCCESS });
              this.activeModal.close("refresh");
            },
            (error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.TEST_PLAN_CREATE_FAILURE });
            }
          );
      }
    }

  }

  handleActionsInTable(event: any) {
    let rowId = event.data.row.id;
    let source = this.dataSource.filter((row: any) => { return row.id === rowId })[0];
    switch (event.data.column.key) {
      case 'agent':
        source.selectedAgent = event.value.value;
        source.agentType = event.value.agentType;
        break;
      case 'browser':
        source.selectedBrowser = event.value.value;
        break;
      case 'version':
        source.selectedVersion = event.value.value;
        break;
    }

  }
  updateGlobalAgent(event: any) {
    this.dataSource.forEach((row: any) => {
      row.selectedAgent = event.value;
      row.agentType = event.agentType;
    });
    this.zorroTableComponent.refreshTableData();

  }
  updateGlobalBrowser(event: any) {
    this.dataSource.forEach((row: any) => { row.selectedBrowser = event.value });
    this.zorroTableComponent.refreshTableData();
  }


  resetFields(isName?: boolean) {
    this.agentValue = '';
    this.browserValue = '';
  }

  buildVersions(data: any[]) {
    let versions: any = [];
    data.forEach((ver: any) => {
      versions.push({
        label: ver.version,
        value: ver.version
      })
    })
    return [{
      label: "",
      options: versions
    }]
  }

  drop(event: any) {
    let row = event.item.data;
    if (!row.dragable) {
      return;
    }

    if (row.tcgTestCaseResultId == null) {
      let errorMessage = MessageEnum.TEST_CASE_MAP_ID_NOT_FOUND + row.displayName
      this.warningMessage = errorMessage;

    } else {
      this.warningMessage = "";
    }

    let versions = this.buildVersions(row.versions);
    this.dataSource = [...this.dataSource, {
      "displayName": row.displayName,
      "selectedBrowser": "",
      "selectedAgent": "",
      "tcgTestCaseResultId": row?.tcgTestCaseResultId,
      "id": `${row.id}_row_${this.dataSource.length + 1}`,
      "idWithoutRow": row.id,
      "selectedVersion": versions[0]?.options?.length ? versions[0].options[versions[0].options.length - 1].value : '',
      "scriptId": row.scriptId,
      "versionList": versions,
      "select": false,
    }];
    this.zorroTableComponent.refreshTableData();
    this.resetFields();
  }

  handleSelect(event: any) {
    this.selectedRows = [];
    event.data.forEach((row: any) => {
      if (row.select) {
        this.selectedRows.push(row.id);
      }
    })
  }

  modifyTree() {
    let key = this.treeSearch.toLowerCase();
    let result: any = []
    this.treeData.forEach((node: any) => {
      let filterResult;
      let isNameContains;
      if (node.children?.length) {
        filterResult = [...node.children].filter((child: any) => {
          return child.name.toLowerCase().includes(key)
        });
        isNameContains = node.displayName.toLowerCase().includes(key);
        if (filterResult.length || isNameContains) {
          result.push({ ...node, children: filterResult })
        }
      } else if (node.name.toLowerCase().includes(key)) {
        result.push({ ...node });
      }
    });
    this.searchResults = [...result];
  }

  ngOnDestroy(): void {
    this.dataSource = [];
  }

  isNameExists(name: string, excludeId?: string): boolean {
    const normalizedName = name?.toLowerCase()?.trim();
    return this.testPlansList
      ?.filter((page: any) => page?.id !== excludeId) // exclude current editing item
      ?.some(
        (page: any) => page?.plan_name?.toLowerCase()?.trim() === normalizedName
      );
  }

}
